import { memo, useCallback, useMemo } from 'react'
import { IconButton, useIMask } from '@cargomart/ui'
import IconMinusSmall from 'assets/minus-small.svg?react'
import IconPlus from 'assets/plus.svg?react'
import cn from 'classnames'
import { SetterVoidReturn } from 'helpers/types'
import { Masked } from 'imask'
import { IInputProps, Input } from 'shared/components/Input'
import * as helpersNumber from 'shared/helpers/number'

export interface INumberInputProps<InputProps extends {}> extends IInputProps<InputProps> {
  onChange: SetterVoidReturn<string>
  autoFocus?: boolean
  isChangeOnMax?: boolean
  max?: string | number
  min?: string | number
  /** удалять незначащие нули после запятой */
  normalizeZeros?: boolean
  outerClassName?: string
  /** добавлять незначащие нули после запятой */
  padFractionalZeros?: boolean
  /** разделитель целой части */
  radix?: string
  //
  /** количество цифр после запятой */
  scale?: number
  /** может ли число быть отрицательным */
  signed?: boolean
  step?: string | number
  /** разделитель для разрядов */
  thousandsSeparator?: string
  value?: string | number
}

const NumberInputNoMemo = <InputProps extends {}>({
  size,
  value = '',
  scale = 0,
  padFractionalZeros = false,
  normalizeZeros = false,
  thousandsSeparator = ' ',
  radix = ',',
  max,
  min,
  step,
  signed,
  isChangeOnMax,
  onChange,
  outerClassName,
  ...props
}: INumberInputProps<InputProps>) => {
  const handleIncrease = useCallback(() => {
    if (!step) {
      return
    }

    const newValue = helpersNumber.plus(value, step)

    if (max && helpersNumber.compare(newValue, max) > 0) {
      onChange(`${max}`)

      return
    }

    onChange(newValue)
  }, [onChange, max, step, value])

  const handleReduce = useCallback(() => {
    if (!step) {
      return
    }
    const newValue = helpersNumber.minus(value, step)

    if (min !== undefined && helpersNumber.compare(newValue, min) < 0) {
      onChange(`${min}`)

      return
    }

    onChange(newValue)
  }, [onChange, min, step, value])

  // ну тут мега кастыль для нужд работы логики isChangeOnMax
  const validate = useCallback(
    (_: string, mask: Masked<any>) => {
      if (isChangeOnMax && max && mask.unmaskedValue && helpersNumber.compare(mask.unmaskedValue, max) > 0) {
        /*
        ВАЖНО:
        - это не пдходящие место для этого кода, это метод валидации а не изменений
        - мы вызываем внутрений метод imask resolve
          https://github.com/uNmAnNeR/imaskjs/blob/322decb322d6253aa56c620d945c4a77b774a447/packages/imask/src/masked/base.js#L116

          Это нужно чтобы в случае если введеное значение пользователем которое больше максимального округлялось
       */

        mask.resolve(String(max))
      }

      return true
    },
    [isChangeOnMax, max]
  )

  const canReduce = useMemo(() => {
    if (!step) {
      return false
    }

    if (min !== undefined) {
      try {
        return helpersNumber.compare(value, min) > 0
      } catch {
        return false
      }
    }

    return true
  }, [min, step, value])

  const canIncrease = useMemo(() => {
    if (!step) {
      return false
    }

    if (max) {
      try {
        return helpersNumber.compare(value, max) < 0
      } catch {
        return false
      }
    }

    return true
  }, [max, step, value])

  const { ref } = useIMask(
    {
      mask: Number,
      min: min === 0 || min ? +min : undefined,
      validate,
      max: isChangeOnMax && !max ? undefined : Number(max),
      radix,
      thousandsSeparator,
      mapToRadix: ['.', 'б', 'ю'],
      scale,
      padFractionalZeros,
      normalizeZeros,
      signed
    },
    { onAccept: onChange },
    { value: String(value), unmask: true }
  )

  return (
    <div className={cn('flex', outerClassName)}>
      <Input inputRef={ref} size={size} {...props} />
      {!!step && (
        <IconButton disabled={!canReduce} onClick={handleReduce} size={size}>
          <IconMinusSmall />
        </IconButton>
      )}
      {!!step && (
        <IconButton disabled={!canIncrease} onClick={handleIncrease} size={size}>
          <IconPlus />
        </IconButton>
      )}
    </div>
  )
}

export const NumberInput = memo(NumberInputNoMemo) as typeof NumberInputNoMemo
